import { clampRect } from '@/core/geometry'
import { drawSourceToImageData, isImageDataSource } from '@/core/image'
import { recognitionMetricKeys, type CaptureProfile, type LocatedMetricRegion, type NormalizedRect, type RecognitionMetricKey } from '@/domain/types'
import type { LayoutDetection, LayoutSearchOptions } from '../types'
import { refinedTemplateError, templateError, type UiTemplate } from './template'
import templates from './layout-templates.json'

export interface WorldsAnchor { x: number; y: number; sx: number; sy: number }
const labels = { hp: [templates.hp, -51, 0], mp: [templates.df, 252, 0], exp: [templates.exp, -51, 27] } as const
const slots = { level: [-382, 3, 65, 40], jobName: [-287, -2, 222, 24],
  hp: [-5, -1, 249, 28], mp: [297, -1, 249, 28], exp: [-5, 27, 551, 26] } as const
function check(options: LayoutSearchOptions) {
  if (options.signal?.aborted) throw new DOMException('Worlds layout cancelled', 'AbortError')
  if (Date.now() >= (options.deadlineAt ?? Infinity)) throw new DOMException('Worlds layout timed out', 'TimeoutError')
}
function labelError(f: ImageData, a: WorldsAnchor, metric: 'hp' | 'mp' | 'exp') {
  const [t, dx, dy] = labels[metric]
  return refinedTemplateError(f, t, a.x + dx * a.sx, a.y + dy * a.sy, a.sx, a.sy)
}
function hudMatches(f: ImageData, a: WorldsAnchor): boolean {
  return labelError(f, a, 'hp') < 40 && labelError(f, a, 'mp') < 40 && labelError(f, a, 'exp') < 40
    && refinedTemplateError(f, templates.level, a.x - 432 * a.sx, a.y + 9 * a.sy, a.sx, a.sy) < 42
}
/** Paired resource borders propose scale; the two-row labels AND LV establish identity. */
export function findWorldsHud(f: ImageData, options: LayoutSearchOptions = {}): WorldsAnchor[] {
  const found: WorldsAnchor[] = []
  for (let y = 0; y < f.height; y++) {
    if (!(y % 16)) check(options)
    const runs: { x: number; width: number }[] = []
    let start = -1
    for (let x = 0; x <= f.width; x++) {
      const i = (y * f.width + x) * 4
      const white = x < f.width && Math.min(f.data[i]!, f.data[i + 1]!, f.data[i + 2]!) > 190
      if (white && start < 0) start = x
      if (!white && start >= 0) {
        if (x - start >= 70 && x - start <= 970) runs.push({ x: start, width: x - start })
        start = -1
      }
    }
    for (let i = 0; i < runs.length; i++) for (let j = i + 1; j < runs.length; j++) {
      const left = runs[i]!, right = runs[j]!, sx = (right.x - left.x) / 302
      if (sx < .45 || sx > 4 || Math.abs(left.width - 242 * sx) > 5 * Math.max(1, sx)
        || Math.abs(right.width - 242 * sx) > 5 * Math.max(1, sx)
        || found.some(a => Math.abs(a.x - left.x) < 8 * sx && Math.abs(a.y - y) < 8 * sx)) continue
      for (const sy of [sx, ...Array.from({ length: 9 }, (_, k) => sx * (.8 + k * .05))]) {
        const a = { x: left.x, y, sx, sy }
        if (hudMatches(f, a)) { found.push(a); break }
      }
    }
  }
  // Capture compression can erase the long white highlight. Yellow LV ink
  // proposes positions cheaply; every candidate still needs all resource labels.
  const point = templates.level.probe.find(p => p[2]! > 170)!
  for (let y = 0; y < f.height; y++) {
    if (!(y % 16)) check(options)
    for (let x = 0; x < f.width; x++) {
      const i = (y * f.width + x) * 4, r = f.data[i]!, g = f.data[i + 1]!, b = f.data[i + 2]!
      if (r < 150 || g < 140 || Math.min(r, g) - b < 60) continue
      for (const s of [.5, 2 / 3, .75, 1, 1.25, 1.5, 2, 2.25, 3, 4]) {
        const lx = x - (point[0]! + .5) * s + .5, ly = y - (point[1]! + .5) * s + .5
        const a = { x: lx + 432 * s, y: ly - 9 * s, sx: s, sy: s }
        if (found.some(p => Math.abs(p.x - a.x) < 10 * s && Math.abs(p.y - a.y) < 10 * s)) continue
        if (templateError(f, templates.level, lx, ly, s, s, true) > 40
          || templateError(f, templates.level, lx, ly, s) > 42) continue
        if (hudMatches(f, a)) found.push(a)
      }
    }
  }
  return found
}
function region(f: { width: number; height: number }, x: number, y: number, width: number, height: number): LocatedMetricRegion | undefined {
  if (x < 0 || y < 0 || x + width > f.width + .5 || y + height > f.height + .5) return undefined
  const rect = clampRect({ x: x / f.width, y: y / f.height, width: width / f.width, height: height / f.height })
  return { rect, textRect: rect, confidence: .95 }
}
export function worldsHudRegions(f: ImageData, a: WorldsAnchor): LayoutDetection {
  const regions: LayoutDetection['regions'] = {}
  for (const [metric, [dx, dy, w, h]] of Object.entries(slots)) {
    const text = region(f, a.x + dx * a.sx, a.y + dy * a.sy, w * a.sx, h * a.sy)
    if (text) regions[metric as keyof typeof slots] = text
  }
  return { hud: clampRect({ x: (a.x - 445 * a.sx) / f.width, y: (a.y - 3 * a.sy) / f.height,
    width: 1000 * a.sx / f.width, height: 56 * a.sy / f.height }), regions,
    diagnostics: { durationMs: 0, candidates: 1, anchors: 4, scaleX: a.sx, scaleY: a.sy } }
}
function fromRegion(f: { width: number; height: number }, r: LocatedMetricRegion, metric: keyof typeof slots): WorldsAnchor {
  const [dx, dy, w, h] = slots[metric], rect = r.textRect ?? r.rect
  const sx = rect.width * f.width / w, sy = rect.height * f.height / h
  return { x: rect.x * f.width - dx * sx, y: rect.y * f.height - dy * sy, sx, sy }
}
export function validWorldsBar(source: CanvasImageSource | ImageData, width: number, height: number,
  metric: 'hp' | 'mp' | 'exp', r: LocatedMetricRegion): boolean {
  const f = isImageDataSource(source) ? source : drawSourceToImageData(source, width, height)
  return labelError(f, fromRegion(f, r, metric), metric) < 40
}
function inventoryMatches(f: ImageData, x: number, y: number, sx: number, sy: number): boolean {
  return refinedTemplateError(f, templates.inventory, x, y, sx, sy) < 35
    && refinedTemplateError(f, templates.coin, x - 89 * sx, y + 495 * sy, sx, sy) < 35
    && refinedTemplateError(f, templates.currency, x - 60 * sx, y + 495 * sy, sx, sy) < 35
}
export function worldsInventoryVisible(f: ImageData, area: NormalizedRect): boolean {
  const sx = area.width * f.width / 147, sy = area.height * f.height / 27
  return inventoryMatches(f, area.x * f.width - 2 * sx, area.y * f.height - 493 * sy, sx, sy)
}
function findInventory(f: ImageData, a: WorldsAnchor, options: LayoutSearchOptions): LocatedMetricRegion | undefined {
  const { sx, sy } = a
  let result: LocatedMetricRegion | undefined
  for (let y = 0; y < f.height - 522 * sy; y++) {
    if (!(y % 16)) check(options)
    for (let x = 89 * sx; x < f.width - templates.inventory.width * sx; x++) {
      if (templateError(f, templates.inventory as UiTemplate, x, y, sx, sy, true) > 32
        || !inventoryMatches(f, x, y, sx, sy)) continue
      const r = region(f, x + 2 * sx, y + 493 * sy, 147 * sx, 27 * sy)
      if (result && r && Math.abs(result.rect.x - r.rect.x) * f.width > 10 * sx) return undefined
      if (r) result = r
      x += templates.inventory.width * sx / 2
    }
  }
  return result
}
export function validateWorldsLayout(f: ImageData, detection: LayoutDetection): LayoutDetection {
  const regions = { ...detection.regions }
  const hp = regions.hp, exp = regions.exp
  const a = hp ? fromRegion(f, hp, 'hp') : exp ? fromRegion(f, exp, 'exp') : null
  for (const metric of ['hp', 'mp', 'exp'] as const) if (regions[metric] && !validWorldsBar(f, f.width, f.height, metric, regions[metric]!)) delete regions[metric]
  if (!a || !hudMatches(f, a)) { delete regions.level; delete regions.jobName }
  if (regions.mesos && !worldsInventoryVisible(f, regions.mesos.textRect ?? regions.mesos.rect)) delete regions.mesos
  return { ...detection, regions }
}
export function locateWorldsLayout(f: ImageData, metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, options: LayoutSearchOptions = {}): LayoutDetection {
  const startedAt = performance.now()
  check(options)
  const found = findWorldsHud(f, options)
  if (found.length !== 1) return { regions: {}, diagnostics: { durationMs: performance.now() - startedAt,
    candidates: found.length, reason: found.length > 1 ? 'multiple-scenes' : 'not-found' } }
  const result = worldsHudRegions(f, found[0]!)
  if (metrics.includes('mesos')) {
    try {
      const inventory = findInventory(f, found[0]!, { ...options, deadlineAt: Math.min(options.deadlineAt ?? Infinity, Date.now() + (options.mesosBudgetMs ?? 1500)) })
      if (inventory) result.regions.mesos = inventory
    } catch (error) {
      // An optional inventory search must not discard the independently verified HUD.
      if (!(error instanceof DOMException) || error.name !== 'TimeoutError') throw error
    }
  }
  return { ...result, regions: Object.fromEntries(Object.entries(result.regions).filter(([metric]) => metrics.includes(metric as RecognitionMetricKey))),
    supportRegions: result.regions, diagnostics: { ...result.diagnostics!, durationMs: performance.now() - startedAt } }
}
export function locatedWorldsJobRect(profile: CaptureProfile): NormalizedRect | undefined {
  return profile.autoRois?.jobName?.textRect ?? profile.autoRois?.jobName?.rect
}
